import React from 'react';
import {
  Table,
  Switch,
  Checkbox,
  TableRow as Row,
  TableHead as Head,
  TableBody as Body,
  TableCell as Cell,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import './css/access.css';
import Button from '@mui/material/Button';
import { parseType } from '../../utils/parseType';
import { CustomDialog } from '../Component/Dialog';
import { send_request } from '../../utils/Request';
import useProjectStore from './useProjectStore';
import { getCurrentUser } from 'aws-amplify/auth';
import { withParams } from '../../Routes';
import { getColourFromString } from '../../utils/ColourNameGenerator';
import { ListItemChip } from '../../utils/ListItemChip';
import { TextField, Autocomplete } from '@mui/material';
import WarningBox from '../pages/CommonStyledComponents/WarningBox';

class Access extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      users: [],
      components: [],
      executors: [],
      loading: false,
      delete: [],
      disable: false,
      error: false,
      loadError: false,
      accountStatus: '',
      initialExecutors: [],
      usersWhoCanAccess: [],
      userNameArray: [],
    };
  }

  componentDidMount() {
    this.loadAccessData();
  }

  loadAccessData = async () => {
    this.setState({ loading: true });

    try {
      const json_execs = await send_request(
        'authz-service/get_executors_by_project_id/' + this.props.projectId,
        '',
        '',
        'get'
      );

      //get a list of mixed project and component executors out of the db
      this.setState({ executors: json_execs.data });
      this.setState({ initialExecutors: json_execs.data });

      const json_components = await send_request(
        `project-service/project/get_access_components/${this.props.projectId}`
      );

      if (json_components.data.length == 0) {
        this.setState({ error: true, loading: false });
        return;
      }

      //replace project executors with component executors
      let newExecutors = [];
      const deleteList = [];

      this.state.executors.forEach((executor, key) => {
        if (executor.componentId == null) {
          //this is a project executor

          json_components.data.forEach((component, inx) => {
            //for each component create a component executor
            if (component.type != 'webhook') {
              let newExecutor = {
                componentId: component.componentId,
                user: executor.user,
                project: { projectId: this.props.projectId },
              };

              newExecutors.push(newExecutor);
            }
          });

          deleteList.push(executor); //delete project executor
        }
      });

      //check if there are project executors
      let projectExecutors = this.state.executors.filter(exec => {
        return exec.componentId == null;
      });

      let finalExecutors;
      if (projectExecutors.length > 0) {
        //replace project executors with component executors
        finalExecutors = newExecutors;
      } else {
        //use data from the request
        finalExecutors = this.state.executors;
      }

      //check if component is public
      const processedComponents = json_components.data.map((component, key) => {
        // Create a new component object instead of mutating the original
        const updatedComponent = { ...component };
        updatedComponent.isPublic = false;

        //check for executors for this component
        let executorsForThisComponent = finalExecutors.filter(executor => {
          return executor.componentId == component.componentId;
        });

        if (executorsForThisComponent.length > 0) {
          updatedComponent.isPublic = false;
        } else {
          updatedComponent.isPublic = true;
        }

        if (component.type == 'webhook') {
          updatedComponent.isPublic = true;
        }

        return updatedComponent;
      });

      // Set all the state at once to avoid race conditions
      this.setState({
        components: processedComponents,
        executors: finalExecutors,
        delete: [...this.state.delete, ...deleteList],
      });

      const json_users = await send_request('authz-service/get-users', '', '', 'get');
      let enabledUsers = json_users.data.filter(user => {
        return user.roleDisabled != true;
      });
      this.setState({ users: enabledUsers });

      const userNameArray = [];
      enabledUsers.forEach(user => {
        if (user.roleEditor && !(user.roleDisabled || user.roleAdministrator || user.roleOwner)) {
          // only allow editors with access to workflows
          userNameArray.push({ displayName: user.userName, userId: user.userId });
        }
      });
      this.setState({ userNameArray: userNameArray });

      const userAccessResponse = await send_request(
        'authz-service/get_users_access_by_project_id/' + this.props.projectId,
        '',
        '',
        'get'
      );

      if (userAccessResponse.data) {
        this.setState({ usersWhoCanAccess: userAccessResponse.data });
      }

      try {
        const user = await getCurrentUser();
        if (user) {
          if (user.signInDetails?.loginId.includes('@')) {
            this.setState({
              accountStatus: 'GOOGLE',
            });
          } else {
            this.setState({
              accountStatus: 'USERNAME',
            });
          }
        }
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    } catch (error) {
      // A failed request is not the same as an empty workflow. Surface a
      // distinct load-error state so we don't falsely claim "no forms".
      console.error('Error loading access data:', error);
      this.setState({ loadError: true });
    } finally {
      this.setState({ loading: false });
    }
  }

  handleSwitch = (key, event) => {
    //this switches between component being public and private
    this.state.components[key].isPublic = !this.state.components[key].isPublic;
    this.setState({ components: this.state.components });

    if (this.state.components[key].isPublic) {
      //if component is set to Public
      let executorsForThisComponent = this.state.executors.filter(executor => {
        return executor.componentId == this.state.components[key].componentId;
      });

      //delete component executors
      this.state.delete.push.apply(this.state.delete, executorsForThisComponent);

      executorsForThisComponent.map((executor, id) => {
        let indx = this.state.executors.indexOf(executor);
        if (indx != -1) this.state.executors.splice(indx, 1);
      });
      this.setState({ executors: this.state.executors });
    }

    this.setState({
      delete: this.state.delete,
      components: this.state.components,
      executors: this.state.executors,
    });
  };

  handleRetry = () => {
    this.setState({ loadError: false, error: false, loading: true }, () => {
      this.loadAccessData();
    });
  };

  handleBack = () => {
    //clear out state
    this.setState({
      loading: false,
      users: [],
      components: [],
      executors: [],
      error: false,
      loadError: false,
    });
    this.props.close();
  };

  handleSave = async saveExecutors => {
    //save
    if (saveExecutors) {
      try {
        await this.grantPermission();
      } catch (error) {
        console.error('Error granting permission:', error);
      }
    }
    try {
      await this.setUserWhoCanAccessTheWorkflow();
    } catch (error) {
      console.error('Setting user access error:', error);
    }
    await send_request(
      'project-service/project/evict_project_folder_cache/' + this.props.projectId
    );
    this.setState({ loading: true });
    await this.deletePermission();
    this.handleBack();
    this.props.handleRerenderBadge();
  };

  deletePermission = async () => {
    if (this.state.delete.length > 0) {
      const executorIds = this.state.delete.map(executor => executor.executorId).filter(id => id);
      return await send_request(
        'authz-service/delete_users_as_executors',
        '',
        { executorIds },
        'delete'
      )
        .then(value => {})
        .catch(err => console.log(err));
    }
  };
  setUserWhoCanAccessTheWorkflow = async () => {
    let userAccessDto = {
      projectId: this.props.projectId,
      userIds: this.state.usersWhoCanAccess.map(user => user.userId),
    };
    return await send_request(
      'authz-service/set_users_who_can_access_workflow',
      userAccessDto,
      '',
      'post'
    )
      .then(value => {})
      .catch(err => console.log(err));
  };

  grantPermission = async () => {
    const newExecutors = [];

    for (let executor of this.state.executors) {
      if (!executor.executorId) {
        let component = this.state.components.find(component => {
          return executor.componentId == component.componentId;
        });

        if (!component.isPublic) {
          let newExecutor = {
            userId: executor.user.userId,
            componentId: executor.componentId,
          };
          newExecutors.push(newExecutor);
        }
      }
    }

    return await send_request(
      'authz-service/set_users_as_executor/' + this.props.projectId,
      newExecutors,
      '',
      'post'
    )
      .then(value => {})
      .catch(err => console.log(err));
  };

  handleCheckbox = (component_key, user_key, checked) => {
    if (checked) {
      //the check box is empty
      //this means we are removing this executor from the executors array

      //1. find executors that can execute this component
      let executorsWithThisComponent = this.state.executors.filter(executor => {
        return executor.componentId == this.state.components[component_key].componentId;
      });
      //2. find an executor that has the correct user
      let executor = executorsWithThisComponent.find(executor => {
        return executor.user.userName == this.state.users[user_key].userName;
      });
      //3. find the index in the executors array
      let index = this.state.executors.indexOf(executor);
      //4. add to the deletion array
      if (this.state.delete.indexOf(executor) == -1) {
        this.state.delete.push(this.state.executors[index]);
      }
      //5. remove the executor from the array
      this.state.executors.splice(index, 1);

      this.setState({
        executors: this.state.executors,
        delete: this.state.delete,
      });
    } else {
      //the check box is checked

      //1. create a new executor
      let executor = {
        componentId: this.state.components[component_key].componentId,
        user: this.state.users[user_key],
        project: { projectId: this.props.projectId },
      };
      //2. add to the executors
      this.state.executors.push(executor);

      //3. check if the executor already in the deletion array
      let toDelete = this.state.delete.find(executor => {
        return (
          executor.user.userName == this.state.users[user_key].userName &&
          executor.componentId == this.state.components[component_key].componentId
        );
      });
      let indexToDelete = this.state.delete.indexOf(toDelete);
      if (indexToDelete != -1) {
        this.state.delete.splice(indexToDelete, 1);
        this.setState({ delete: this.state.delete });
      }
      this.setState({ executors: this.state.executors });
    }
  };

  handleRedirect = component => {
    let node = useProjectStore.getState().getAggregate().components.find(cp => {
      return cp.component_id == component.componentId;
    });
    const node_id = node.view.node_id;
    let type = component.type;
    if (type == 'form') type = 'formbuilder';
    this.props.navigate(window.location.pathname + `/${type}/${component.componentId || ''}`, {
      search: `?node_id=${node_id}`,
    });
  };

  handleRerender = () => {
    this.props.handleRerenderBadge();
  };

  onUserChange = user => {
    this.setState({ usersWhoCanAccess: user });
  };

  renderUserAccessAutocomplete = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Editors who can view and edit this workflow
          </Typography>
        </Box>
        <Box>
          <Autocomplete
            options={this.state.userNameArray}
            clearOnBlur
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                let item = option.displayName ?? option;
                return (
                  <ListItemChip
                    {...getTagProps({ index })}
                    label={item}
                    colour={getColourFromString(item)}
                  />
                );
              })
            }
            size={'small'}
            multiple
            freeSolo={true}
            openOnFocus={true}
            getOptionLabel={option => {
              return option.displayName;
            }}
            color={'info'}
            value={this.state.usersWhoCanAccess}
            onChange={(event, chip) => {
              this.onUserChange(chip);
            }}
            renderInput={params => {
              return (
                <TextField
                  {...params}
                  placeholder={'Select Editor users'}
                  size={'small'}
                  variant="outlined"
                />
              );
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', pb: 2 }}>
          Owner and Administrators have access to the workflow by default
        </Typography>
      </Box>
    );
  };

  render() {
    let { components, users, executors, loading, error, loadError, accountStatus } = this.state;
    const { published } = this.props;

    if (loading) {
      return (
        <CustomDialog
          isOpen={loading}
          size={'lg'}
          title={'Access Control'}
          contents={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress size={40} />
            </Box>
          }
        />
      );
    }

    if (loadError) {
      return (
        <CustomDialog
          size={'md'}
          isOpen={this.state.loadError}
          title={'Access Control'}
          contents={
            <WarningBox
              text="Couldn't load access settings for this workflow. Please try again."
              width="100%"
              horizontal="flex-start"
            />
          }
          buttons={
            <>
              <Button variant={'outlined'} color={'info'} onClick={this.handleBack}>
                Close
              </Button>
              <Button variant={'contained'} color={'primary'} onClick={this.handleRetry}>
                Retry
              </Button>
            </>
          }
        />
      );
    }

    if (error) {
      return (
        <CustomDialog
          size={'md'}
          isOpen={this.state.error}
          title={'Access Control'}
          contents={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {this.renderUserAccessAutocomplete()}
              <WarningBox
                text="There are no forms or tasks in this workflow"
                width="100%"
                horizontal="flex-start"
              />
            </Box>
          }
          buttons={
            <>
              <Button variant={'outlined'} color={'info'} onClick={this.handleBack}>
                Close
              </Button>
              <Button
                variant={'contained'}
                color={'primary'}
                disabled={loading}
                onClick={() => {
                  this.handleSave(false);
                }}
              >
                {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                Save
              </Button>
            </>
          }
        />
      );
    }

    return (
      <CustomDialog
        isOpen={this.props.open && !this.state.error}
        size={'lg'}
        title={'Access Control'}
        contents={
          <>
            {this.renderUserAccessAutocomplete()}
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: 'text.primary', mt: 1, mb: 1 }}
            >
              Who can access interface components (Forms and Tasks)
            </Typography>
            <Table
              sx={{
                '& .MuiTableCell-root': {
                  color: 'text.primary',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                },
                '& .MuiTableCell-head': {
                  color: 'text.secondary',
                  fontWeight: 600,
                },
              }}
            >
              <Head>
                <Row>
                  <Cell>User</Cell>
                  {components.map((component, key) => {
                    let name = component.name;
                    if (!name) name = component.componentId;
                    return (
                      <Cell key={key} align={'center'}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            className={'clickable trim-name'}
                            onClick={() => this.handleRedirect(component)}
                            title={name}
                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            {name}
                          </Typography>
                          <Typography
                            variant="caption"
                            className={'trim-name'}
                            sx={{ color: 'text.secondary' }}
                          >
                            {parseType(component.type)}
                          </Typography>
                        </Box>
                      </Cell>
                    );
                  })}
                </Row>
                <Row>
                  <Cell className={'tableCell'}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>Public</Box>
                  </Cell>
                  {components.map((component, key) => {
                    return (
                      <Cell align={'center'} key={key} className={'tableCell'}>
                        <Switch
                          value={component.isPublic}
                          checked={component.isPublic}
                          disabled={component.type == 'webhook'}
                          onChange={() => this.handleSwitch(key, event)}
                          color="primary"
                        />
                      </Cell>
                    );
                  })}
                </Row>
              </Head>
              <Body>
                {users.map((user, user_key) => {
                  return (
                    <Row
                      key={user_key}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Cell className={'tableCell'}>{user.displayName}</Cell>
                      {components.map((component, component_key) => {
                        let checked = executors.some(executor => {
                          return (
                            executor.user.userId == user.userId &&
                            (executor.componentId == component.componentId ||
                              (executor.project.projectId == this.props.projectId &&
                                executor.componentId == null))
                          );
                        });

                        return (
                          <Cell align={'center'} key={component_key} className={'tableCell'}>
                            <Checkbox
                              disabled={component.isPublic}
                              value={component.isPublic ? false : checked}
                              checked={component.isPublic ? false : checked}
                              onChange={() => {
                                this.handleCheckbox(component_key, user_key, checked);
                              }}
                            />
                          </Cell>
                        );
                      })}
                    </Row>
                  );
                })}
              </Body>
            </Table>
          </>
        }
        buttons={
          <>
            <Button
              variant={'outlined'}
              color={'info'}
              disabled={loading}
              onClick={this.handleBack}
            >
              Close
            </Button>
            <Button
              variant={'contained'}
              color={'primary'}
              disabled={loading}
              onClick={() => {
                this.handleSave(true);
              }}
            >
              {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
              Save
            </Button>
          </>
        }
      />
    );
  }
}
export default withParams(Access);
